import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { db } from "../../utils/Firebase-Config";
import { doc, getDoc, collection, getDocs, query, orderBy } from "firebase/firestore";
import { UserAuth } from "../../utils/AuthContext";
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import "./Portfolio.css"

const deobfuscateUrl = (scrambledUrl) => {
  if (!scrambledUrl) return "";
  try {
    const decoded = atob(scrambledUrl);
    return decoded.split("").reverse().join("");
  } catch (err) {
    return scrambledUrl;
  }
};

function Portfolio() {
  const { username: pathUsername } = useParams();
  const { user } = UserAuth();

  const portfolio = useRef();
  const profile = useRef();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isOwner, setIsOwner] = useState(false);
  const [portfolioItems, setPortfolioItems] = useState([]);

// Slide navigation states & mobile touch tracking
const [activeIndex, setActiveIndex] = useState(0);
const [touchStartX, setTouchStartX] = useState(null);
const [touchEndX, setTouchEndX] = useState(null);


  useEffect(() => {
    if (!pathUsername) return;
    const fetchMetadata = async () => {
      try {
        const cleanId = pathUsername.toLowerCase().replace(/[^a-z0-9]/g, "");
        const docRef = doc(db, "portfolios", cleanId);
        const docSnap = await getDoc(docRef);

        if (docSnap.exists()) {
          setData({ id: docSnap.id, data: docSnap.data() });
        } else {
          setError("This portfolio does not exist or has been removed.");
        }
      } catch (err) {
        console.error("Error loading portfolio metadata:", err);
        setError("Failed to load portfolio database records.");
      } finally {
        setLoading(false);
      }
    };
    fetchMetadata();
  }, [pathUsername]);

  useEffect(() => {
    if (!data || !data.id) return;

    const fetchItems = async () => {
      try {
        const itemsRef = collection(db, "portfolios", data.id, "portfolio");
        const q = query(itemsRef, orderBy("createdAt", "desc"));
        const querySnapshot = await getDocs(q);

        const items = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setPortfolioItems(items);
      } catch (err) {
        console.error("Error loading portfolio items:", err);
      }
    };

    fetchItems();
  }, [data]);

  useEffect(() => {
    if (!data || !data.data) return;
    const { ownerUid } = data.data; 
    if (user && user.uid === ownerUid) {
      setIsOwner(true);
    }
  }, [user, data]);

  useEffect(() => {
    if (!data || !data.id) return;

    const detectAndLogScrapers = async () => {
      const userAgent = navigator.userAgent;
      let bot = null;

      if (userAgent.includes("GPTBot")) {
        bot = { name: "OpenAI (GPTBot)", type: "Model Training", reason: "Ingesting portfolio images to train future generative visual models like DALL-E." };
      } else if (userAgent.includes("OAI-SearchBot")) {
        bot = { name: "OpenAI Search", type: "Real-Time Indexing", reason: "Scanning images to display them directly as conversational search results inside ChatGPT." };
      } else if (userAgent.includes("ChatGPT-User")) {
        bot = { name: "ChatGPT User Fetch", type: "Direct User Prompt", reason: "An active user pasted your URL directly into a prompt window, requesting ChatGPT analyze the page content." };
      } else if (userAgent.includes("Claude-User")) {
        bot = { name: "Claude User Fetch", type: "Direct User Prompt", reason: "An active user pasted your URL directly into Claude, requesting the assistant analyze the page content." };
      } else if (userAgent.includes("Claude-SearchBot")) {
        bot = { name: "Claude Search", type: "Real-Time Indexing", reason: "Scanning layout properties to populate the retrieval layers behind conversational search answers." };
      } else if (userAgent.includes("ClaudeBot") || userAgent.includes("anthropic-ai")) {
        bot = { name: "Anthropic Claude", type: "Model Training", reason: "Harvesting style arrays and creative imagery to refine Claude multimodal neural structures." };
      } else if (userAgent.includes("Google-Extended") || userAgent.includes("Googlebot")) {
        bot = { name: "Google Gemini Engine", type: "Model Training", reason: "Extracting portfolio layouts to populate Gemini structural generative image weights." };
      } else if (userAgent.includes("Applebot-Extended")) {
        bot = { name: "Apple Intelligence", type: "Model Training", reason: "Indexing art samples to train on-device generative software across Apple ecosystems." };
      } else if (userAgent.includes("Perplexity-User")) {
        bot = { name: "Perplexity User Fetch", type: "Direct User Prompt", reason: "An active user pasted your URL directly into a prompt window, requesting Perplexity analyze the page content." };
      } else if (userAgent.includes("PerplexityBot")) {
        bot = { name: "Perplexity AI", type: "Real-Time Indexing", reason: "Scraping live structural properties to display references within conversational search cards." };
      } else if (userAgent.includes("meta-externalfetcher")) {
        bot = { name: "Meta AI Fetcher", type: "Real-Time Indexing", reason: "Scanning live structural properties to display references or answers within Meta AI conversational features." };
      } else if (userAgent.includes("meta-externalagent") || userAgent.includes("facebookexternalhit")) {
        bot = { name: "Meta AI", type: "Model Training", reason: "Infiltrating art sites to optimize text-to-image engines across Instagram, WhatsApp, and Facebook features." };
      } else if (userAgent.includes("Amazonbot")) {
        bot = { name: "Amazon Bot", type: "Model Training", reason: "Scanning layout properties to improve Alexa answer capabilities and train Amazon retail AI models." };
      } else if (userAgent.includes("cohere-ai")) {
        bot = { name: "Cohere Bot", type: "Model Training", reason: "Harvesting style arrays and web elements to train enterprise Cohere LLM weights." };
      } else if (userAgent.includes("YouBot")) {
        bot = { name: "YouBot AI", type: "Real-Time Indexing", reason: "Scanning live properties to index and reference within You.com conversational search queries." };
      } else if (userAgent.includes("CCBot")) {
        bot = { name: "Common Crawl Bot", type: "Mass Ingestion Data Broker", reason: "Copying open web elements to compile massive public datasets, heavily abused by unregulated AI training labs." };
      } else if (userAgent.includes("Bytespider")) {
        bot = { name: "ByteDance AI", type: "Mass Ingestion", reason: "Harvesting raw media assets for TikTok parent organization AI tools and video diffusion frameworks." };
      }

      const isStealthScraper = 
        navigator.webdriver || 
        (window.innerWidth === 0 && window.innerHeight === 0) || 
        (!navigator.languages || navigator.languages.length === 0);

      if (!bot && isStealthScraper) {
        bot = {
          name: "Stealth Scraping Agent",
          type: "Stealth Crawler",
          reason: "Masking identity signatures via automated headers while scraping web layouts."
        };
      }

      if (bot) {
        const dbUrl = "https://logbot-sl5e5lb5lq-uc.a.run.app";
        const payload = JSON.stringify({
          siteId: data.id,
          botName: bot.name,
          botType: bot.type,
          botReason: bot.reason,
          pageUrl: window.location.href
        });

        try {
          if (navigator.sendBeacon) {
            navigator.sendBeacon(dbUrl, payload);
          } else {
            await fetch(dbUrl, { method: "POST", body: payload, headers: { "Content-Type": "text/plain" }});
          }
        } catch (err) {
          console.error("Telemetry transmission failed:", err);
        }
      }
    };

    detectAndLogScrapers();
  }, [data]);


const nextSlide = () => setActiveIndex((p) => (p + 1) % portfolioItems.length);
const prevSlide = () => setActiveIndex((p) => (p - 1 + portfolioItems.length) % portfolioItems.length);

// Swipe Handlers
const handleTouchStart = (e) => setTouchStartX(e.targetTouches[0].clientX);
const handleTouchMove = (e) => setTouchEndX(e.targetTouches[0].clientX);
const handleTouchEnd = () => {
  if (!touchStartX || !touchEndX) return;
  const diff = touchStartX - touchEndX;
  if (Math.abs(diff) > 50) { // 50px threshold
    diff > 0 ? nextSlide() : prevSlide();
  }
  setTouchStartX(null);
  setTouchEndX(null);
};

if (loading) return <div style={{ padding: "50px", textAlign: "center" }}>Loading...</div>;
if (error) return <div style={{ padding: "50px", textAlign: "center", color: "#ef4444" }}>{error}</div>;
if (!data || !data.data) return <div style={{ padding: "50px", textAlign: "center" }}>No portfolio data found.</div>;

const { displayName, bio, profileImage, themeColor, username } = data.data;
const primaryColor = themeColor || "#4f46e5";

return (
  <div className="portfolio-layout">
    {isOwner && (
      <div className="console-btn">
        <button onClick={() => window.location.href = "/home"}>Dashboard</button>
      </div>
    )}

    <div className="portfolio-hero" />

      
      {/* SLID TO THE SIDE */}
      <div className="portfolio-card-wrapper" ref={profile}>
        <div className="card-content-box">
          {profileImage ? (
            <img src={profileImage} alt={displayName} className="avatar-img" />
          ) : (
            <div className="avatar-fallback" style={{ backgroundColor: primaryColor }}>
              {displayName ? displayName.charAt(0).toUpperCase() : "?"}
            </div>
          )}
          <h1 className="name">{displayName || username}</h1>
          <p className="handle">@{username}</p>
          <hr className="divider-line" />
          <p className="bio">{bio || "Welcome to my gallery!"}</p>
          
        </div>
      </div>

      {/* CAROUSEL */}
      {portfolioItems.length > 0 && (
      <div className="photo-grid-wrapper" ref={portfolio}>

        <div 
          className="carousel-viewport"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
        >

        <div className="photo-grid-bar" onClick={() => {
              portfolio.current.classList.toggle("photo-grid-active");
              profile.current.classList.toggle("portfolio-card-wrapper-active");
        }}></div>
        <div className="photo-grid-bar" style={{ right: 0}} onClick={() => {
              portfolio.current.classList.toggle("photo-grid-active");
              profile.current.classList.toggle("portfolio-card-wrapper-active");
        }}></div>

          <div className="photo-grid" style={{ transform: `translateX(-${activeIndex * 100}%)` }}>
            {portfolioItems.map((item) => (
              <div key={item.id} className="carousel-slide">
                <div className="image-container">
                  <img
                    src={deobfuscateUrl(item.imageUrl)} 
                    alt={item.title}
                    className="carousel-image"
                    loading="lazy"
                  /> 
                          
                </div>

                <span id="title" className="carousel-title" >{item.title}</span>
              </div>
            ))}
          </div>

          {portfolioItems.length > 1 && (   
            <div className="carousel-controls">
              <button  onClick={prevSlide}><NavigateBeforeIcon /></button>
              <button  onClick={nextSlide}><NavigateNextIcon /></button>
            </div>
          )}
        </div>
      </div>
      )}


    <div className="port-footer">
      <span>Powered by Mvelio • Portfolio: {data.id}</span>
    </div>

  </div>
);


}

export default Portfolio;
